import { useEffect, useMemo, useState } from "react";
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  TextInput,
  Group,
  Button,
  Badge,
  Stack,
  Select,
  Pagination,
  Modal,
  Tooltip,
  Skeleton,
  Alert,
  Checkbox,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconSearch,
  IconRefresh,
  IconUserPlus,
  IconTrash,
  IconShieldCheck,
  IconInfoCircle,
} from "@tabler/icons-react";
import { Endpoints } from "../../../lib/endpoints";
import { PaginatedResponse } from "../../../lib/types";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../hooks/usePerformRequest";
import PermissionGuard from "../../../components/PermissionGuard";
import { AdminPermissions, HttpCodes } from "../../../lib/data";

interface IAllowedUserProps {
  _id: string;
  email: string;
  addedByEmail?: string;
  note?: string;
  createdAt: string;
}

const AllowedUsers = () => {
  useEffect(() => {
    document.title = "Allowed Users | Virel Admin";
  }, []);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newNote, setNewNote] = useState("");
  const [sendInvite, setSendInvite] = useState(true);
  const [isAdding, setIsAdding] = useState(false);

  const [removeTarget, setRemoveTarget] = useState<IAllowedUserProps | null>(
    null,
  );
  const [isRemoving, setIsRemoving] = useState(false);

  const listEndpoint = useMemo(
    () =>
      Endpoints.GetAllowedUsers({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
      }),
    [currentPage, pageSize, debouncedSearch],
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, reloadData } = usePerformRequest<
    PaginatedResponse<IAllowedUserProps[]>
  >({
    method: listEndpoint.method,
    route: listEndpoint.route,
    showError: false,
  });

  const entries = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / pageSize));
  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd =
    totalRecords === 0 ? 0 : Math.min(currentPage * pageSize, totalRecords);

  const formatDate = (value?: string | Date) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value.toString();
    return date.toLocaleString();
  };

  const handleAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      notifications.show({
        color: "red",
        title: "Invalid email",
        message: "Please enter a valid email address",
      });
      return;
    }
    setIsAdding(true);
    const endpoint = Endpoints.AddAllowedUser();
    const r = await PerformRequest<{
      message?: string;
      data?: { inviteEmailSent?: boolean };
    }>({
      method: endpoint.method,
      route: endpoint.route,
      data: { email, note: newNote.trim() || undefined, sendInvite },
    });
    setIsAdding(false);

    if (r.status === HttpCodes.Created || r.status === HttpCodes.Success) {
      const emailFailed = sendInvite && !r.data?.data?.inviteEmailSent;
      notifications.show({
        color: emailFailed ? "yellow" : "green",
        title: "Access granted",
        message: emailFailed
          ? `${email} can sign in immediately, but the invitation email FAILED — send it manually`
          : sendInvite
            ? `${email} can sign in immediately — invitation email sent`
            : `${email} can sign in immediately — no redeploy needed`,
      });
      setAddModalOpen(false);
      setNewEmail("");
      setNewNote("");
      setSendInvite(true);
      reloadData();
    } else {
      notifications.show({
        color: "red",
        title: "Could not add email",
        message: r.data?.message || r.message || "Something went wrong",
      });
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setIsRemoving(true);
    const endpoint = Endpoints.RemoveAllowedUser(removeTarget.email);
    const r = await PerformRequest<{ message?: string }>({
      method: endpoint.method,
      route: endpoint.route,
    });
    setIsRemoving(false);
    setRemoveTarget(null);

    if (r.status === HttpCodes.Success) {
      notifications.show({
        color: "green",
        title: "Access removed",
        message: r.data?.message || `${removeTarget.email} removed`,
      });
      reloadData();
    } else {
      notifications.show({
        color: "red",
        title: "Could not remove email",
        message: r.data?.message || r.message || "Something went wrong",
      });
    }
  };

  const rows = entries.map((entry) => (
    <Table.Tr key={entry._id}>
      <Table.Td>
        <Text fw={500} size="sm">
          {entry.email}
        </Text>
      </Table.Td>
      <Table.Td>
        {entry.addedByEmail === "seed:code-allowlist" ? (
          <Badge color="gray" variant="light" size="sm">
            Original list
          </Badge>
        ) : (
          <Text size="sm" c="dimmed">
            {entry.addedByEmail || "—"}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {entry.note || "—"}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{formatDate(entry.createdAt)}</Text>
      </Table.Td>
      <Table.Td>
        <Tooltip label="Remove access" withArrow>
          <Button
            variant="subtle"
            color="red"
            size="xs"
            onClick={() => setRemoveTarget(entry)}
          >
            <IconTrash size={16} />
          </Button>
        </Tooltip>
      </Table.Td>
    </Table.Tr>
  ));

  const skeletonRows = Array.from({ length: 6 }).map((_, idx) => (
    <Table.Tr key={`skeleton-${idx}`}>
      <Table.Td>
        <Skeleton height={10} width="80%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={10} width="60%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={10} width="70%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={10} width="50%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={10} width={30} />
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <PermissionGuard
      permission={AdminPermissions.MANAGE_USERS}
      fallback={
        <Container>
          <Text>Unauthorized</Text>
        </Container>
      }
    >
      <div className="allowed-users-page">
        <Container size="xl">
          <Stack gap="lg">
            <Group justify="space-between" align="flex-end">
              <div>
                <Title order={1} className="page-title">
                  Allowed Users
                </Title>
                <Text size="sm" c="dimmed" mt={4}>
                  The closed-beta guest list — only these emails can register
                  or sign in to the app
                </Text>
              </div>
              <Button
                leftSection={<IconUserPlus size={16} />}
                onClick={() => setAddModalOpen(true)}
              >
                Add Email
              </Button>
            </Group>

            <Alert
              icon={<IconShieldCheck size={16} />}
              color="green"
              variant="light"
            >
              Adding an email here takes effect immediately — no code change or
              redeploy. The anti-spam gate stays on: anyone not on this list is
              still blocked.
            </Alert>

            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group gap="md" mb="md" className="table-toolbar">
                <TextInput
                  placeholder="Search by email..."
                  leftSection={<IconSearch size={16} />}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                  style={{ flex: 1 }}
                  size="md"
                />
                <Group gap="xs" align="center">
                  <Text size="sm" c="dimmed">
                    List Per Page
                  </Text>
                  <Select
                    data={["10", "20", "50", "100"]}
                    value={String(pageSize)}
                    onChange={(value) => {
                      const next = Number(value) || 20;
                      setPageSize(next);
                      setCurrentPage(1);
                    }}
                    size="sm"
                    w={100}
                  />
                </Group>
              </Group>

              <div className="table-container">
                <Table
                  striped
                  highlightOnHover
                  verticalSpacing="sm"
                  className="data-grid"
                  withColumnBorders
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Added By</Table.Th>
                      <Table.Th>Note</Table.Th>
                      <Table.Th>Added</Table.Th>
                      <Table.Th w={60}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {isLoading ? (
                      skeletonRows
                    ) : rows.length > 0 ? (
                      rows
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={5} style={{ textAlign: "center" }}>
                          <Stack align="center" gap="xs" py="xl">
                            <IconInfoCircle
                              size={40}
                              stroke={1.5}
                              color="gray"
                            />
                            <Text c="dimmed">
                              {debouncedSearch
                                ? "No emails match your search"
                                : "The database list is empty — run the migration script to import the current guest list"}
                            </Text>
                          </Stack>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </div>

              <Group justify="center" mt="md">
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => reloadData()}
                >
                  Reload
                </Button>
              </Group>

              {totalPages > 1 && (
                <Group justify="center" mt="md">
                  <Pagination
                    value={currentPage}
                    onChange={setCurrentPage}
                    total={totalPages}
                    size="sm"
                  />
                </Group>
              )}

              <Group
                justify="space-between"
                mt="md"
                pt="md"
                style={{ borderTop: "1px solid #e2e8f0" }}
              >
                <Text size="sm" c="dimmed">
                  Showing {rangeStart} to {rangeEnd} of {totalRecords} allowed
                  emails
                </Text>
              </Group>
            </Paper>
          </Stack>
        </Container>
      </div>

      <Modal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title="Add email to the guest list"
        centered
      >
        <Stack gap="md">
          <TextInput
            label="Email address"
            placeholder="person@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.currentTarget.value)}
            required
            data-autofocus
          />
          <TextInput
            label="Note (optional)"
            placeholder="e.g. name, who referred them"
            value={newNote}
            onChange={(e) => setNewNote(e.currentTarget.value)}
          />
          <Checkbox
            label="Send the official invitation email"
            description="They receive the VIREL welcome email with download link and sign-in steps"
            checked={sendInvite}
            onChange={(e) => setSendInvite(e.currentTarget.checked)}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setAddModalOpen(false)}
              disabled={isAdding}
            >
              Cancel
            </Button>
            <Button onClick={handleAdd} loading={isAdding}>
              Grant Access
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title="Remove access?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            <b>{removeTarget?.email}</b> will no longer be able to register or
            sign in. Their account and balance are not deleted — only the door
            closes.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setRemoveTarget(null)}
              disabled={isRemoving}
            >
              Cancel
            </Button>
            <Button color="red" onClick={handleRemove} loading={isRemoving}>
              Remove Access
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PermissionGuard>
  );
};

export default AllowedUsers;
